
import Vue from 'vue';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { PageViewport } from 'pdfjs-dist/types/src/display/display_utils';

interface RenderTask {
  cancel();
}

export default Vue.extend({
  name: 'PDFPage',

  props: {
    page: { type: Object as () => PDFPageProxy, required: true },
    width: { type: Number, required: true },
    scale: { type: Number, default: 1.000 },
    webgl: { type: Boolean, default: true },
    hidpi: { type: Boolean, default: true },
    ratio: { type: Number },
  },

  data() {
    return {
      viewport: this.page.getViewport({ scale: this.scale }),
      renderTask: undefined as RenderTask | undefined,
    };
  },

  mounted() {
    this.drawPage();
  },

  beforeDestroy() {
    this.cleanup();
  },

  watch: {
    page(_newPage, oldPage) {
      if (oldPage) {
        this.cleanup();
      }
    },
    width() {
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.width * this.ratio}px`;
    },
  },

  computed: {
    pageNumber(): number {
      return this.page.pageNumber;
    },
    canvas(): HTMLCanvasElement {
      return this.$el as HTMLCanvasElement;
    },
  },

  methods: {

    drawPage() {
      if (this.renderTask) {
        return;
      }
      /** inspiration: https://github.com/mozilla/pdf.js/blob/master/web/pdf_page_view.js#L568 .printOnCanvas() */

      try {
        // const start = window.performance.now();
        const { canvas } = this;
        canvas.setAttribute('hidden', 'hidden');
        const parent = canvas.parentElement;
        if (!parent) {
          throw new Error('No canvas parent found');
        }
        const canvasContext = canvas.getContext('2d', { alpha: false });
        if (!canvasContext) {
          throw new Error('No 2D canvas context found');
        }
        const dim = this.canvasDimensions(canvasContext, this.viewport, parent.clientWidth);
        // console.log(dim);
        canvas.width = dim.draw.width;
        canvas.height = dim.draw.height;
        canvas.style.width = `${dim.display.width}px`;
        canvas.style.height = `${dim.display.height}px`;
        const renderContext = {
          canvasContext,
          viewport: dim.viewport,
          enableWebGL: this.webgl,
        };
        const renderTask = this.page.render(renderContext);
        this.renderTask = renderTask;
        renderTask
          .promise.then(() => {
            canvas.style.display = 'block';
            canvas.removeAttribute('hidden');
            // console.log(`pdf page-rendered in ${window.performance.now() - start}`);
            this.$emit('rendered');
          })
          .catch(this.onFailure);
      } catch (e) {
        this.onFailure(e);
      }
    },

    canvasDimensions(canvasContext: CanvasRenderingContext2D, viewport: PageViewport, width: number) {
      let scale = (Math.ceil(width) / viewport.width);
      if (this.hidpi) {
        // supersample canvas to make it look crisp on high DPI displays, this costs more but avoids blurryness on retina displays
        scale *= deviceOutputScale(canvasContext);
      }
      const ratio = this.ratio || viewport.height / viewport.width;
      const scaled = this.page.getViewport({ scale });
      return {
        viewport: scaled,
        draw: {
          width: scaled.width,
          height: scaled.height,
        },
        display: {
          width,
          height: width * ratio,
        },
      };
    },

    onFailure(e?: any) {
      this.cleanup();
      this.$emit('failure', e);
    },

    cleanup() {
      if (this.renderTask) {
        this.renderTask.cancel();
        this.renderTask = undefined;
      }
      if (this.page) {
        this.page.cleanup();
      }
    },
  },

});

function deviceOutputScale(ctx) {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const backingStoreRatio = ctx.webkitBackingStorePixelRatio
    || ctx.mozBackingStorePixelRatio
    || ctx.msBackingStorePixelRatio
    || ctx.oBackingStorePixelRatio
    || ctx.backingStorePixelRatio
    || 1;
  const pixelRatio = devicePixelRatio / backingStoreRatio;
  return pixelRatio;
}

